import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchAllGiftCards } from '../api/giftCardsAPI';

const GiftCardContext = createContext();

export const useGiftCard = () => {
    const context = useContext(GiftCardContext);
    if (!context) {
        throw new Error('useGiftCard must be used within a GiftCardProvider');
    }
    return context;
};

export const GiftCardProvider = ({ children }) => {
    const [giftCards, setGiftCards] = useState([]);
    const [purchaseHistory, setPurchaseHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch initial data
    useEffect(() => {
        fetchGiftCardsData();
        fetchPurchaseHistory();
    }, []);

    // Fetch gift cards from API
    const fetchGiftCardsData = async () => {
        try {
            setLoading(true);
            const { data, error } = await fetchAllGiftCards();
            
            if (error) {
                throw new Error(error.message);
            }
            
            setGiftCards(data);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch gift cards: ' + err.message);
            setLoading(false);
        }
    };

    // Fetch purchase history from API
    const fetchPurchaseHistory = async () => {
        try {
            setLoading(true);
            // TODO: Replace with actual API call when purchase functionality is implemented
            const mockPurchaseHistory = [
                {
                    id: 1,
                    purchaseDate: '2024-01-30',
                    userType: 'registered',
                    userName: 'John Doe',
                    email: 'john@example.com',
                    giftCard: {
                        carType: 'Sedan',
                        service: 'Full Cleaning',
                        price: 290
                    },
                    status: 'active'
                },
                // Add more mock data as needed
            ];
            setPurchaseHistory(mockPurchaseHistory);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch purchase history');
            setLoading(false);
        }
    };

    // Update gift card
    const updateGiftCard = async (updatedCard) => {
        try {
            setLoading(true);
            // TODO: Replace with actual API call when admin functionality is implemented
            
            setGiftCards(prevCards =>
                prevCards.map(card =>
                    card.carType === updatedCard.carType ? updatedCard : card
                )
            );

            // Refresh purchase history to reflect changes
            await fetchPurchaseHistory();
            setLoading(false);
        } catch (err) {
            setError('Failed to update gift card');
            setLoading(false);
        }
    };

    // Add new gift card
    const addGiftCard = async (newCard) => {
        try {
            setLoading(true);
            // TODO: Replace with actual API call when admin functionality is implemented
            
            setGiftCards(prevCards => [...prevCards, newCard]);
            setLoading(false);
        } catch (err) {
            setError('Failed to add gift card');
            setLoading(false);
        }
    };

    // Delete gift card
    const deleteGiftCard = async (cardType) => {
        try {
            setLoading(true);
            // TODO: Replace with actual API call when admin functionality is implemented
            
            setGiftCards(prevCards =>
                prevCards.filter(card => card.carType !== cardType)
            );
            setLoading(false);
        } catch (err) {
            setError('Failed to delete gift card');
            setLoading(false);
        }
    };

    // Update purchase status
    const updatePurchaseStatus = async (purchaseId, newStatus) => {
        try {
            setLoading(true);
            // TODO: Replace with actual API call when purchase functionality is implemented
            
            setPurchaseHistory(prevHistory =>
                prevHistory.map(purchase =>
                    purchase.id === purchaseId
                        ? { ...purchase, status: newStatus }
                        : purchase
                )
            );
            setLoading(false);
        } catch (err) {
            setError('Failed to update purchase status');
            setLoading(false);
        }
    };

    const value = {
        giftCards,
        purchaseHistory,
        loading,
        error,
        updateGiftCard,
        addGiftCard,
        deleteGiftCard,
        updatePurchaseStatus,
        refreshData: async () => {
            await Promise.all([fetchGiftCardsData(), fetchPurchaseHistory()]);
        }
    };

    return (
        <GiftCardContext.Provider value={value}>
            {children}
        </GiftCardContext.Provider>
    );
}; 